import { NestedFilterInput, NestedFilterItem } from '@apeiron/common'
import { NextImage, SearchEmptyState, TextInput, theme } from '@apeiron/temp'
import {
  Box,
  Modal,
  Slide,
  styled,
  useAutocomplete,
  useMediaQuery
} from '@mui/material'
import SearchSVG from '@public/icons/icon-search.svg'
import ApeironRecycleList from '@src/components/share/apeiron/ApeironRecycleList'
import FilterScrollBox from '@src/components/share/filter/FilterScrollBox'
import MuiPopover from '@src/components/share/mui/Popover'
import * as R from 'ramda'
import {
  HTMLAttributes,
  KeyboardEvent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(Box)`
  margin: 0px 8px;
`

const StyledSearchIcon = styled(NextImage)`
  position: absolute;
  left: 12px;
  height: 45%;
  aspect-ratio: 1;
`

const SearchInput = styled(TextInput)`
  display: flex;
  padding: 4px;
  margin: 0px auto 8px auto;
  width: 90%;

  ${props => props.theme.breakpoints.up('res1024')} {
    margin: 8px auto;
  }

  & {
    background-color: #b7b7b7;
  }

  & .MuiOutlinedInput-input {
    font-size: 16px;
    line-height: 16px;
    padding: 8px 8px 8px 40px;
    background-color: #b7b7b7;
    color: #565857;
  }

  &.active .MuiOutlinedInput-notchedOutline {
    border-color: #ffbf36;
    border-width: 1px;
  }
`

const MobilePopoverContainer = styled(Modal)`
  top: unset;
  height: 80vh;

  border-top-right-radius: 10px;
  border-top-left-radius: 10px;
`

const Popover = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${props => props.theme.breakpoints.up('res1024')} {
    width: 450px;
    max-height: 550px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    width: 100%;
  }
`

const PopoverListBox = styled('ul')`
  padding: 0;
  margin: 0;
  height: 750px;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
`

const PopoverHeader = styled(Box)`
  padding: 0px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ListItem = styled('li')`
  display: block;
  flex: none;
  padding: 0;
  padding-left: 14px;
  padding-right: 36px;
  cursor: pointer;

  &.Mui-focused {
    background: #bfbfbf;
  }
`

const NoResult = styled(SearchEmptyState)`
  height: 100%;
  padding: 50px 0px;
  color: #b1b3b3;
`

const ItemPopover = <T extends NestedFilterInput>(props: PopoverProps<T>) => {
  const {
    additionalComponentInPopup,
    items,
    filterInput,
    autocompleteOptions,
    autocompleteListbox,
    onFilterItem,
    renderItem,
    renderPopupFilter
  } = props

  const { t } = useTranslation()

  const filterComponent = useMemo(
    () => renderPopupFilter(filterInput, onFilterItem),
    [filterInput, onFilterItem, renderPopupFilter]
  )

  const handleOnRenderItem = useCallback(
    (measureRef: (ref: Element | null) => void, index: number) => {
      const item = items[index]

      return (
        <ListItem
          ref={measureRef}
          {...autocompleteOptions({ option: item, index })}
          key={item.uniqueKey}
          data-index={item.uniqueKey}
        >
          {renderItem(item)}
        </ListItem>
      )
    },
    [autocompleteOptions, items, renderItem]
  )

  return (
    <Popover>
      <PopoverHeader>
        {filterComponent}
        {additionalComponentInPopup}
      </PopoverHeader>
      {R.isEmpty(items) ? (
        <NoResult description={t('filter.sections.skill.no_result')} />
      ) : (
        <PopoverListBox {...autocompleteListbox()}>
          <ApeironRecycleList
            total={items.length}
            onRenderItem={handleOnRenderItem}
          />
        </PopoverListBox>
      )}
    </Popover>
  )
}

const NestedFilterPopup = <T extends NestedFilterInput>(props: Props<T>) => {
  const {
    additionalComponentInPopup,
    title,
    allItems,
    defaultSearchParam,
    items,
    searchPlaceholder,
    onChange,
    searchByText,
    searchItems,
    renderItem,
    renderPopupFilter
  } = props

  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const [filteredItems, setFilteredItems] = useState<NestedFilterItem[]>([])

  const [isPopoverOpened, setIsPopoverOpened] = useState(false)

  const [filterInput, setFilterInput] = useState(defaultSearchParam)

  const mobile = useMediaQuery(theme.breakpoints.down('res1024'))

  const handleOnOpen = useCallback(() => {
    setIsPopoverOpened(true)
  }, [])

  const handleOnChange = useCallback(
    (_: any, selected: NestedFilterItem[]) => {
      if (onChange) {
        onChange(selected)
      }
    },
    [onChange]
  )

  const handleOnBlur = useCallback(() => {
    setIsPopoverOpened(false)
  }, [])

  const handleOnKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      Promise.resolve().then(() => {
        sentinelRef.current?.focus()
      })
    }
  }, [])

  const handleOnFilterItem = useCallback(
    (input: T) => {
      setFilterInput(input)

      const result = searchItems(input)

      setFilteredItems(result)
    },
    [searchItems]
  )

  useEffect(() => {
    if (allItems) {
      setFilteredItems(allItems)
    }
  }, [allItems])

  const filteredDedupedItems = useMemo(() => {
    return R.reject((item: NestedFilterItem) => {
      const uniqueKeys = R.pluck('uniqueKey', items)

      return R.includes(item.uniqueKey, uniqueKeys)
    }, filteredItems)
  }, [filteredItems, items])

  const {
    anchorEl,
    groupedOptions,
    getInputProps,
    getListboxProps,
    getOptionProps,
    setAnchorEl
  } = useAutocomplete({
    clearOnBlur: false,
    filterSelectedOptions: true,
    multiple: true,
    open: isPopoverOpened,
    options: filteredDedupedItems,
    value: items,
    getOptionLabel: searchByText,
    onChange: handleOnChange,
    onOpen: handleOnOpen
  })

  return (
    <Container>
      <SearchInput
        className={isPopoverOpened ? 'active' : ''}
        placeholder={searchPlaceholder}
        startAdornment={<StyledSearchIcon src={SearchSVG} />}
        inputProps={{ ...getInputProps(), id: `${title}-search-bar` }}
        inputRef={setAnchorEl}
        onKeyDown={handleOnKeyDown}
      />
      {!mobile ? (
        <MuiPopover
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          marginThreshold={16}
          open={isPopoverOpened}
          keepMounted
          onClose={handleOnBlur}
        >
          <FilterScrollBox onClose={handleOnBlur} title={title}>
            <SearchInput
              className={'active'}
              placeholder={searchPlaceholder}
              startAdornment={<StyledSearchIcon src={SearchSVG} />}
              inputProps={{
                ...getInputProps(),
                id: `${title}-popup-search-bar`
              }}
              onKeyDown={handleOnKeyDown}
            />
            <ItemPopover
              additionalComponentInPopup={additionalComponentInPopup}
              filterInput={filterInput}
              items={groupedOptions as NestedFilterItem[]}
              autocompleteListbox={getListboxProps}
              autocompleteOptions={getOptionProps}
              onFilterItem={handleOnFilterItem}
              renderItem={renderItem}
              renderPopupFilter={renderPopupFilter}
            />
          </FilterScrollBox>
        </MuiPopover>
      ) : (
        <MobilePopoverContainer
          open={isPopoverOpened}
          onClose={handleOnBlur}
          disablePortal
        >
          <Slide direction='up' in={isPopoverOpened}>
            <FilterScrollBox onClose={handleOnBlur} title={title} noScroll>
              <SearchInput
                className={'active'}
                placeholder={searchPlaceholder}
                startAdornment={<StyledSearchIcon src={SearchSVG} />}
                inputProps={{
                  ...getInputProps(),
                  id: `${title}-popup-search-bar`
                }}
                onKeyDown={handleOnKeyDown}
              />
              <ItemPopover
                additionalComponentInPopup={additionalComponentInPopup}
                filterInput={filterInput}
                items={groupedOptions as NestedFilterItem[]}
                autocompleteListbox={getListboxProps}
                autocompleteOptions={getOptionProps}
                onFilterItem={handleOnFilterItem}
                renderItem={renderItem}
                renderPopupFilter={renderPopupFilter}
              />
            </FilterScrollBox>
          </Slide>
        </MobilePopoverContainer>
      )}
    </Container>
  )
}

type PopoverProps<T extends NestedFilterInput> = {
  additionalComponentInPopup?: ReactElement
  items: NestedFilterItem[]
  filterInput: T
  autocompleteOptions: ({
    option,
    index
  }: {
    option: NestedFilterItem
    index: number
  }) => HTMLAttributes<HTMLLIElement>
  autocompleteListbox: () => HTMLAttributes<HTMLUListElement>
  onFilterItem: (filterInput: T) => void
  renderItem: (items: NestedFilterItem) => ReactElement
  renderPopupFilter: (
    filterInput: T,
    onFilterItem: (filterInput: T) => void
  ) => ReactElement
}

type Props<T extends NestedFilterInput> = {
  additionalComponentInPopup?: ReactElement
  title?: string
  allItems: NestedFilterItem[]
  defaultSearchParam: T
  items: NestedFilterItem[]
  searchPlaceholder: string
  onChange?: (items: NestedFilterItem[]) => void
  searchByText: (item: NestedFilterItem) => string
  searchItems: (params: T) => NestedFilterItem[]
  renderItem: (items: NestedFilterItem) => ReactElement
  renderPopupFilter: (
    filterInput: T,
    onFilterItem: (filterInput: T) => void
  ) => ReactElement
}

export default NestedFilterPopup
